import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { FormField } from '@/components/FormField';
import { Card, SectionHeader } from '@/components/Card';
import { PhoneInput } from '@/components/PhoneInput';
import { contactsService } from '@/services/contacts.service';
import { User, Building2, MapPin, StickyNote, Plus, Minus, ShieldOff, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import { useCompanyCountry, useLabels } from '@/contexts/WorkspaceContext';

export default function ContactCreate() {
  const t = useLabels();
  const nav = useNavigate();
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    city: '',
    notes: '',
    phone: '',
    countryCode: '',
    metaShareOptedOut: false,
  });
  const [customRows, setCustomRows] = useState<Array<{ k: string; v: string }>>([]);
  const companyCountry = useCompanyCountry();
  const countryCode = form.countryCode || companyCountry;
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const customFields = Object.fromEntries(
        customRows.filter((r) => r.k && r.v).map((r) => [r.k, r.v]),
      );
      await contactsService.create({ ...form, countryCode, customFields });
      toast.success('Contact created');
      nav('/contacts');
    } catch { /* toast shown */ } finally { setBusy(false); }
  };

  return (
    <>
      <Topbar
        title={`Add ${t('contacts', 'singular').toLowerCase()}`}
        breadcrumb={`Audience / ${t('contacts')}`}
        description="Create a new contact record."
        back={
          <button
            onClick={() => nav('/contacts')}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-surface-alt hover:text-text-primary"
            aria-label="Back to contacts"
          >
            <ArrowLeft size={16} />
          </button>
        }
        right={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => nav('/contacts')}>Cancel</Button>
            <Button disabled={busy || !form.firstName.trim() || (!form.phone && !form.email.trim())} onClick={submit}>
              Create contact
            </Button>
          </div>
        }
      />

      <div className="mx-auto w-full max-w-[800px] px-7 py-7 space-y-4">
        {/* Personal info */}
        <Card>
          <SectionHeader title="Personal information" icon={<User size={16} />} className="mb-4" />
          <div className="grid grid-cols-2 gap-3">
            <FormField label="First name" required>
              <Input value={form.firstName} onChange={(e) => setForm((s) => ({ ...s, firstName: e.target.value }))} />
            </FormField>
            <FormField label="Last name">
              <Input value={form.lastName} onChange={(e) => setForm((s) => ({ ...s, lastName: e.target.value }))} />
            </FormField>
          </div>
          <p className="mt-3 text-xs text-text-muted">Add a phone number or email address. Email-only contacts can be saved without a phone.</p>
          <FormField label="Phone">
            <PhoneInput
              value={form.phone}
              defaultCountry={countryCode as any}
              onChange={(v) => setForm((s) => ({ ...s, phone: v.e164, countryCode: v.country ?? s.countryCode }))}
            />
          </FormField>
          <FormField label="Email">
            <Input type="email" value={form.email} onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))} />
          </FormField>
        </Card>

        {/* Company + location */}
        <Card>
          <SectionHeader title="Company & location" icon={<Building2 size={16} />} className="mb-4" />
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Company">
              <Input value={form.company} onChange={(e) => setForm((s) => ({ ...s, company: e.target.value }))} iconLeft={<Building2 size={14} />} />
            </FormField>
            <FormField label="City">
              <Input value={form.city} onChange={(e) => setForm((s) => ({ ...s, city: e.target.value }))} iconLeft={<MapPin size={14} />} />
            </FormField>
          </div>
          <FormField label="Notes">
            <Input value={form.notes} onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))} iconLeft={<StickyNote size={14} />} />
          </FormField>
        </Card>

        {/* Privacy & consent */}
        <Card>
          <SectionHeader title="Privacy & consent" icon={<ShieldOff size={16} />} className="mb-3" />
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-primary-soft"
              checked={form.metaShareOptedOut}
              onChange={(e) => setForm((s) => ({ ...s, metaShareOptedOut: e.target.checked }))}
            />
            <div className="flex-1">
              <span className="text-sm font-semibold text-text-primary">Don't share with Meta</span>
              <p className="mt-0.5 text-xs text-text-muted">
                When ticked, this contact's email + phone are excluded from
                Facebook/Instagram Custom Audience uploads (used for ad
                targeting + lookalikes). General in-CRM use is unaffected.
                Required for GDPR / CCPA when a person opts out of ad sharing.
              </p>
            </div>
          </label>
        </Card>

        {/* Custom fields */}
        <Card>
          <SectionHeader
            title="Custom fields"
            className="mb-4"
            action={
              <Button size="sm" variant="ghost" iconLeft={<Plus size={13} />}
                onClick={() => setCustomRows((r) => [...r, { k: '', v: '' }])}>
                Add field
              </Button>
            }
          />
          {customRows.length === 0 ? (
            <p className="text-sm text-text-muted">No custom fields — click Add field to create one.</p>
          ) : (
            <div className="space-y-2">
              {customRows.map((row, idx) => (
                <div key={idx} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-start">
                  <Input
                    value={row.k}
                    onChange={(e) => setCustomRows((r) => r.map((x, i) => i === idx ? { ...x, k: e.target.value } : x))}
                    placeholder="key (e.g. accountNumber)"
                  />
                  <Input
                    value={row.v}
                    onChange={(e) => setCustomRows((r) => r.map((x, i) => i === idx ? { ...x, v: e.target.value } : x))}
                    placeholder="value"
                  />
                  <button
                    type="button"
                    onClick={() => setCustomRows((r) => r.filter((_, i) => i !== idx))}
                    className="flex h-10 w-10 items-center justify-center rounded-lg text-text-muted hover:bg-danger-soft hover:text-danger transition-colors"
                  >
                    <Minus size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </>
  );
}
